import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Camera, Check, Mic, Video as VideoIcon, X } from 'lucide-react';
import { Button } from '../../../../../../../../ui';
import { CustomSelect } from '../../../../../../../../ui/custom_react_select/custom_select';
import { TextArea } from '../../../../../../../../ui/input/text_area/text_area';
import {
  getAssessmentSettings,
  updateAnswerNotesAndStatus,
} from '../../../../../../../../utils/api_service/assessment_api';
import { Permission } from '../../../../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../../../../utils/hooks/PermissionGate';
import {
  AssessmentQuestionResult,
} from '../../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../../utils/types/react_select_types';

interface SkillQuestionAndAnswerProps {
  assessmentQuestionResult: AssessmentQuestionResult;
  isVideoBlur?: boolean;
  updateDetailedQuestionArray: (
    questionArray: AssessmentQuestionResult
  ) => void;
  setResultOpen: (value: boolean) => void;
  showSystemAnswer?: boolean;
  showVideo?: boolean;
}

export const SkillQuestionAndAnswer: React.FC<SkillQuestionAndAnswerProps> = ({
  assessmentQuestionResult,
  updateDetailedQuestionArray,
  setResultOpen,
  showVideo,
}) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [questionResult, setQuestionResult] =
    useState<AssessmentQuestionResult>(assessmentQuestionResult);
  const [blurVideo, setBlurVideo] = useState<boolean>(false);

  useEffect(() => {
    getAssessmentSettings(assessmentQuestionResult.assessmentId).then(
      (response) => {
        setBlurVideo(response.blurVideo);
      },
    );
  }, [assessmentQuestionResult.assessmentId]);

  const answerSelect: SelectOptions[] = [
    {
      value: 'correct',
      label: 'Answer is Correct',
    },
    {
      value: 'incorrect',
      label: 'Answer is In-Correct',
    },
  ];

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setQuestionResult({
      ...questionResult,
      personalNotes: event.target.value,
    });
  };

  const handleSelectChange = (option: SelectOptions) => {
    if (option.value === 'correct') {
      setQuestionResult({
        ...questionResult,
        score: questionResult.maxScore,
        isCorrect: true,
      });
    } else {
      setQuestionResult({
        ...questionResult,
        score: 0,
        isCorrect: false,
      });
    }
  };

  const saveUpdatedQuestionResult = () => {
    updateAnswerNotesAndStatus(
      questionResult.id,
      questionResult.isCorrect,
      questionResult.personalNotes,
    ).then((response) => {
      if (response.ok) {
        updateDetailedQuestionArray(questionResult);
        setResultOpen(false);
      }
    });
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <>
      {/* Video Modal */}
      <Dialog
        open={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'primary.50',
                border: 1,
                borderColor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VideoIcon size={18} color="#2563eb" />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Video Response
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseVideoModal} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Left Column: Video */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#0f172a',
                  borderRadius: 2,
                  overflow: 'hidden',
                  aspectRatio: '16/9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {blurVideo ? (
                  <Stack alignItems="center" spacing={2} color="#94a3b8">
                    <CircularProgress size={40} color="primary" />
                    <Typography variant="body1" fontWeight={500}>
                      Loading Video...
                    </Typography>
                    <Typography variant="caption">
                      Please wait while we fetch your video.
                    </Typography>
                  </Stack>
                ) : questionResult.videoUrl ? (
                  <Box
                    component="video"
                    controls
                    disablePictureInPicture={blurVideo}
                    src={questionResult.videoUrl}
                    preload="metadata"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Stack alignItems="center" spacing={1} color="#94a3b8">
                    <VideoIcon size={40} />
                    <Typography variant="body2" fontWeight={500}>
                      Video Not Available
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Right Column: Metadata & Transcript */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    textTransform="uppercase"
                  >
                    Total Points
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {questionResult.score || 0}
                  </Typography>
                </Paper>

                {questionResult.userAnswer && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      borderRadius: 1.5,
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      mb={1}
                    >
                      Transcript
                    </Typography>
                    <Box sx={{ overflowY: 'auto', maxHeight: 240, pr: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                      >
                        {questionResult.userAnswer}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <Stack spacing={3}>
        {/* Response Section */}
        <Stack spacing={2}>
          {questionResult.questionType === 'Modern' && showVideo ? (
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#0f172a',
              }}
            >
              {blurVideo && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backdropFilter: 'blur(8px)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Paper
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: 'rgba(255,255,255,0.85)',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.primary"
                      textTransform="uppercase"
                    >
                      Video Blurred
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Box
                component="video"
                controls
                disablePictureInPicture={blurVideo}
                src={questionResult.videoUrl}
                sx={{ width: '100%', aspectRatio: '16/9', display: 'block' }}
              />
            </Paper>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.5,
                        bgcolor: 'success.50',
                        border: 1,
                        borderColor: 'success.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        color="success.main"
                        fontSize="0.65rem"
                      >
                        S
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      System Analysis
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {questionResult.systemAnswer}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.5,
                        bgcolor: 'primary.50',
                        border: 1,
                        borderColor: 'primary.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        color="primary.main"
                        fontSize="0.65rem"
                      >
                        T
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Response Transcript
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {questionResult.userAnswer}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Action Row */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                  bgcolor: 'primary.50',
                  border: 1,
                  borderColor: 'primary.100',
                  borderRadius: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: 0.5,
                      bgcolor: 'primary.main',
                    }}
                  />
                  <Typography variant="body2" fontWeight={700}>
                    Total Points: {questionResult.score || 0}
                  </Typography>
                </Stack>
                <MuiButton
                  size="small"
                  variant="outlined"
                  startIcon={<VideoIcon size={14} />}
                  onClick={() => setIsVideoModalOpen(true)}
                  sx={{
                    bgcolor: 'background.paper',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                  }}
                >
                  View Live Response
                </MuiButton>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  bgcolor: 'warning.50',
                  border: 1,
                  borderColor: 'warning.200',
                  borderRadius: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: 'warning.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Camera size={16} color="#d97706" />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="warning.dark"
                    textTransform="uppercase"
                    display="block"
                  >
                    Movement Detection
                  </Typography>
                  <Typography variant="caption" color="warning.dark">
                    4 instances of significant movement
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Stack>

        {/* Analytics & Reviews */}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Accuracy & Speech Rate */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Performance Analytics
              </Typography>
              <Grid container spacing={2}>
                {/* Accuracy Card */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: 'primary.50',
                          border: 1,
                          borderColor: 'primary.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Check size={14} color="#2563eb" />
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        textTransform="uppercase"
                      >
                        Accuracy
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      {Math.round(assessmentQuestionResult.similarityScore * 100)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(
                        assessmentQuestionResult.similarityScore * 100,
                      )}
                      sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
                    />
                  </Paper>
                </Grid>

                {/* Speech Rate Card */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: 'success.50',
                          border: 1,
                          borderColor: 'success.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Mic size={14} color="#16a34a" />
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        textTransform="uppercase"
                      >
                        Speech Rate
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {Math.round(assessmentQuestionResult.speechRate)}
                      </Typography>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        WPM
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      color="success"
                      value={Math.min(
                        Math.round(assessmentQuestionResult.speechRate / 2),
                        100,
                      )}
                      sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* Review Section */}
            <Grid item xs={12} md={6}>
              <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                  Review & Assessment
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      display="block"
                      mb={0.75}
                    >
                      Personal Notes
                    </Typography>
                    <TextArea
                      name="personal-notes"
                      value={questionResult.personalNotes}
                      onChange={handleChange}
                    />
                  </Box>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        name="change"
                        value={
                          questionResult.isCorrect
                            ? answerSelect[0]
                            : answerSelect[1]
                        }
                        options={answerSelect}
                        onChange={(option: SelectOptions) =>
                          handleSelectChange(option)
                        }
                      />
                    </Box>
                    <Button
                      onClick={saveUpdatedQuestionResult}
                      variant="primary"
                      label="Save Review"
                    />
                  </Stack>
                </Stack>
              </PermissionGate>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </>
  );
};
